import { useContext, useMemo } from 'react';

import { Box, Text, Tooltip } from '@chakra-ui/react';
import { MdChatBubble } from 'react-icons/md';

import { Deal } from '../../gql/dealGql';
import { Deal as GenDeal } from '../../gql/generated/graphql';

import { AbilityContext, TextWidgetContext } from '../../libs/contextLib';
import { getIsClickable } from '../../utils/deals';
import { emptyConversation, getConversationBoxes } from '../../utils/messages';

type TextNotificationBadgeProps = {
  deal?: GenDeal | Deal;
  count?: number;
  size?: number;
  showSimplifiedCount?: boolean;
};

const TextNotificationBadge = ({
  deal,
  count,
  size = 28,
  showSimplifiedCount = false,
}: TextNotificationBadgeProps) => {
  const { setTextDealId, setActiveConversation } = useContext(TextWidgetContext);
  const abilities = useContext(AbilityContext);
  const canOpenWidget = useMemo(() => !!deal && getIsClickable(deal, abilities), [deal, abilities]);
  if (!count) {
    return null;
  }
  const getSimplifiedCount = (originalCount?: number) =>
    originalCount !== undefined && originalCount > 9 ? '9+' : originalCount;

  const countToShow = showSimplifiedCount ? getSimplifiedCount(count) : count;
  const sizePx = `${size}px`;

  return (
    <Box
      pos="relative"
      lineHeight={sizePx}
      cursor={canOpenWidget ? 'pointer' : 'not-allowed'}
      onClick={(e) => {
        e.stopPropagation();
        if (canOpenWidget && deal) {
          if (deal.id) {
            setTextDealId(deal.id);
          }
          setActiveConversation(
            getConversationBoxes({ deal })[0]?.conversation ?? emptyConversation,
          );
        }
      }}
    >
      <Tooltip isDisabled={!count} hasArrow label={`${count} unread`} placement="top">
        <Box pos="relative" w={sizePx} h={sizePx}>
          <MdChatBubble color="#8CC63F" size={size} />
          <Text
            lineHeight="0.5rem"
            fontSize={size > 24 ? '0.75rem' : '0.5rem'}
            pos="absolute"
            top="47.5%"
            left="50%"
            transform="translate(-50%, -50%)"
            color="white"
          >
            {countToShow}
          </Text>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default TextNotificationBadge;
